"use client";

import { FC, ReactNode } from "react";

const BAR = "animate-pulse rounded bg-[rgb(229,229,229)]";

export interface StockListingPageSkeletonProps {
  /** Number of placeholder rows. Should match the server-rendered row count to avoid layout shift. */
  rowCount?: number;
  /** Reserves the desktop table header row (hidden on mobile, same as StockTable). */
  showTableHeader?: boolean;
  /** Static header markup rendered before the client listing hydrates. */
  headerSlot?: ReactNode;
  /** Static mobile controls rendered before the client listing hydrates. */
  mobileHeaderSlot?: ReactNode;
  /** Whether to reserve the Show More control below the initial rows. */
  showMore?: boolean;
}

/**
 * Suspense fallback for StockListingPageClient (PLP: all stocks, 52 week high/low, gainers/losers).
 * Mirrors the exact box model of the real listing (section width, table width, 41.5px header row,
 * 72.5px table rows) so swapping the fallback for real content produces no layout shift.
 */
const StockListingPageSkeleton: FC<StockListingPageSkeletonProps> = ({
  rowCount = 10,
  showTableHeader = true,
  headerSlot,
  mobileHeaderSlot,
  showMore = false,
}) => (
  <div className="flex w-full">
    <aside className="hidden w-[16.5%] shrink-0 lg:block" />
    <section
      className="box-border block w-full max-w-223 border-l-[rgba(0,0,0,0.08)] bg-white leading-[19.2px] md:border-l-3 md:p-3"
      aria-hidden="true"
    >
      {headerSlot}
      {mobileHeaderSlot}
      <div className="w-full px-4 py-1">
        <table className="w-full max-w-217 border-collapse text-sm md:text-base">
          {showTableHeader && (
            <thead className="hidden md:table-header-group">
              <tr className="box-border h-[41.5px] w-full max-w-217 border-b border-[rgb(229,229,229)] py-3 leading-[19.2px]">
                <th className="w-[60%] text-left text-sm font-normal">
                  <span className={`${BAR} block h-3.5 w-27.5`} />
                </th>
                <th className="w-[40%] text-right text-sm font-normal">
                  <span className={`${BAR} ml-auto block h-3.5 w-8`} />
                </th>
              </tr>
            </thead>
          )}

          <tbody>
            {Array.from({ length: rowCount }, (_, index) => `stock-listing-skeleton-row-${index}`).map(
              (rowKey) => (
                <tr
                  key={rowKey}
                  className="box-border h-[72.5px] border-b border-b-[rgb(229,229,229)] leading-[19.2px]"
                >
                  <td className="py-4 text-right" colSpan={2}>
                    <section className="flex w-full items-center justify-between gap-3">
                      <div className="block w-full min-w-0">
                        <section className="flex w-full items-center justify-between gap-3">
                          <div className="flex min-w-0 flex-col items-start">
                            <span className={`${BAR} mb-1 block h-4.75 w-23`} />
                            <span className={`${BAR} block h-[16.5px] w-37.5`} />
                          </div>

                          <div className="flex flex-col items-end">
                            <span className={`${BAR} mb-1 block h-4.75 w-18`} />
                            <span className={`${BAR} block h-[16.5px] w-24`} />
                          </div>
                        </section>
                      </div>

                      <span className={`${BAR} ml-2 block h-5 w-5 shrink-0`} />
                    </section>
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>
        {showMore && (
          <div className="mt-4 flex max-w-212.5 justify-center gap-4 p-4 pt-0">
            <span className={`${BAR} block h-10 w-full max-w-64.5`} />
          </div>
        )}
      </div>
    </section>
  </div>
);

export default StockListingPageSkeleton;
